import classNames from "classnames";
import styles from "./styles.module.scss";
import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";
import { ESenderRole } from "../../../../setup/api/services/contract/models";
import { GET_PARTY_STATUSES } from "../../../../components/contract/payeeTable/constants";
import TooltipWrapper from "../../../../components/common/TooltipWrapper";
import useWindowSize from "../../../../hooks/getPageSize";
import PartyCard from "./PartyCard";
import { isArabicOnly } from "../../../../setup/util/isArabic";

export type PartyRow = {
  id: string;
  name: string;
  phoneNumber: string;
  amount: number | string;
  role?: string;
  type: string;
  status?: string;
  image?: string;
  cashoutTime?: string;
  referenceNumber?: string;
  paymentMethod?: string;
  onBehalfByName?: string;
};

type PartyTableProps = {
  rows: PartyRow[];
  emptyMessage?: string;
  className?: string;
  columns?: {
    showStatus?: boolean;
    showCashoutTime?: boolean;
    showReferenceNumber?: boolean;
    showPaymentMethod?: boolean;
    showOnBehalf?: boolean;
    /**
     * Keep a column's track (and its share of the row width) without rendering
     * anything in it. Stacked tables of the same contract only line up when
     * every one of them declares the same grid, so a table that lacks a column
     * its neighbours have still has to reserve the space for it.
     */
    reservePaymentMethod?: boolean;
    reserveOnBehalf?: boolean;
  };
};

/**
 * Read-only parties table of the milestone details page, ported from the
 * business portal's details-page mode of its party table.
 */
const PartyTable = ({
  rows,
  className,
  emptyMessage,
  columns,
}: PartyTableProps) => {
  const { t } = useTranslation();
  const [pageSize] = useWindowSize();
  const [isExpanded, setIsExpanded] = useState(false);

  const rowsToRender = useMemo(() => {
    if (rows.length <= 3 || isExpanded) return rows;
    return rows.slice(0, 3);
  }, [rows, isExpanded]);

  const toggleIsExpanded = () => {
    setIsExpanded((prev) => !prev);
  };

  const showStatus = columns?.showStatus ?? true;
  const showCashoutTime = columns?.showCashoutTime;
  const showReferenceNumber = columns?.showReferenceNumber;
  const showPaymentMethod = columns?.showPaymentMethod;
  const showOnBehalf = columns?.showOnBehalf;

  // A column exists in the grid either because this table fills it, or because
  // a sibling table does and we have to keep the shared columns aligned.
  const hasPaymentMethodColumn =
    !!showPaymentMethod || !!columns?.reservePaymentMethod;
  const hasOnBehalfColumn = !!showOnBehalf || !!columns?.reserveOnBehalf;

  const partiesStatuses = GET_PARTY_STATUSES(t);

  const effectiveRows =
    rows && rows.length > 0
      ? rows
      : new Array(3).fill(null).map(
          (_, i) =>
            ({
              id: `empty-${i}`,
              name: "",
              phoneNumber: "",
              amount: "",
              role: "",
            } as PartyRow)
        );

  const effectiveEmptyMessage =
    emptyMessage || t("lbl_party_table_empty_message");

  const gridTemplate = useMemo(
    () => `
  3.5fr ${hasPaymentMethodColumn ? "2fr" : "5fr"} ${
      hasPaymentMethodColumn ? "1.5fr" : ""
    } minmax(100px, 1.5fr) ${showCashoutTime ? "4fr" : ""} ${
      showReferenceNumber ? "3fr" : ""
    } ${showStatus ? "2fr" : ""} ${hasOnBehalfColumn ? "2fr" : ""}
`,
    [
      hasPaymentMethodColumn,
      showCashoutTime,
      showReferenceNumber,
      showStatus,
      hasOnBehalfColumn,
    ]
  );

  if (pageSize < 900) {
    return (
      <div className={styles.list}>
        {rowsToRender.map((row) => (
          <PartyCard
            key={row.id}
            name={row.name}
            phone={row.phoneNumber}
            image={row.image}
            amount={String(row.amount)}
            status={row.status}
          />
        ))}
        {rows.length > 3 && (
          <p
            className={classNames(styles.more_link, styles.link)}
            onClick={toggleIsExpanded}>
            {isExpanded
              ? t("lbl_contract_details_btn_show_less")
              : "+ " +
                String(rows.length - 3) +
                " " +
                t("lbl_order_created_more")}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className={classNames(styles.tableWrapper, className)}>
      <div className={styles.table}>
        <div className={styles.tableHeader}>
          <div
            className={classNames(styles.row, styles.headerRow)}
            style={{ gridTemplateColumns: gridTemplate }}>
            <div className={classNames(styles.cell, styles.colName)}>
              {t("lbl_name")}
            </div>
            <div className={classNames(styles.cell, styles.colPhone)}>
              {rows?.length === 1 && rows[0]?.role === ESenderRole.BROKER
                ? t("lbl_identifier")
                : t("lbl_mobile_number")}
            </div>
            {hasPaymentMethodColumn && (
              <div className={classNames(styles.cell, styles.colWafyFee)}>
                {showPaymentMethod ? "طريقة الدفع" : ""}
              </div>
            )}
            <div className={classNames(styles.cell, styles.colValue)}>
              {showCashoutTime ? t("lbl_transfer_amount") : t("lbl_value")}
            </div>
            {showCashoutTime && (
              <div className={classNames(styles.cell, styles.colWafyFee)}>
                {t("lbl_transfer_time")}
              </div>
            )}
            {showReferenceNumber && (
              <div className={classNames(styles.cell, styles.colWafyFee)}>
                {t("lbl_reference_number")}
              </div>
            )}
            {showStatus && (
              <div className={classNames(styles.cell, styles.colStatus)}>
                {t("lbl_status")}
              </div>
            )}
            {hasOnBehalfColumn && (
              <div className={classNames(styles.cell, styles.colWafyFee)}>
                {showOnBehalf ? t("lbl_on_behalf") : ""}
              </div>
            )}
          </div>
        </div>

        <div className={styles.tableBody}>
          {effectiveRows.map((row, index) => {
            const isZebra = index % 2 === 1;

            if (effectiveRows?.[0]?.id?.startsWith("empty") && index === 1)
              return (
                <p
                  key={`empty-1`}
                  style={{ marginTop: 0, textAlign: "center" }}>
                  {effectiveEmptyMessage}
                </p>
              );

            return (
              <div key={`${row.id}-${index}`} className={styles.group}>
                <div
                  className={classNames(styles.row, isZebra && styles.zebra)}
                  style={{ gridTemplateColumns: gridTemplate }}>
                  <div className={classNames(styles.cell, styles.colName)}>
                    <div className={styles.cellWithError}>
                      <TooltipWrapper
                        id="name_id_tooltip"
                        content={
                          row.name || (row.phoneNumber ? t("lbl_no_join") : "")
                        }>
                        <span
                          style={{
                            direction: isArabicOnly(row.name) ? "rtl" : "ltr",
                            textAlign: isArabicOnly(row.name) ? "start" : "end",
                            paddingLeft: "16px",
                          }}
                          className={styles.primaryText}>
                          {row.name ||
                            (row.phoneNumber ? t("lbl_no_join") : "")}
                        </span>
                      </TooltipWrapper>
                    </div>
                  </div>
                  <div className={classNames(styles.cell, styles.colPhone)}>
                    <div className={styles.cellWithError}>
                      <TooltipWrapper
                        arabicDir
                        id="phone_number_id_tooltip"
                        content={row.phoneNumber || ""}>
                        <span
                          style={{ direction: "ltr" }}
                          className={styles.monoText}>
                          {row?.id?.startsWith("empty")
                            ? ""
                            : row?.role === ESenderRole.BROKER
                            ? "-"
                            : row.phoneNumber || "-"}
                        </span>
                      </TooltipWrapper>
                    </div>
                  </div>
                  {hasPaymentMethodColumn && (
                    <div className={classNames(styles.cell, styles.colName)}>
                      <span className={classNames(styles.normalText)}>
                        {showPaymentMethod ? row?.paymentMethod ?? "-" : ""}
                      </span>
                    </div>
                  )}
                  <div className={classNames(styles.cell, styles.colValue)}>
                    <div className={styles.cellWithError}>
                      <TooltipWrapper
                        id="amount_id_tooltip"
                        content={row?.amount?.toLocaleString() ?? ""}>
                        <span
                          className={styles.amountText}
                          style={{ fontWeight: 600, fontSize: "14px" }}>
                          <span
                            style={{
                              color: "#000000",
                              marginLeft: "4px",
                            }}>
                            {row?.amount?.toLocaleString()}
                          </span>{" "}
                          {row?.amount ? t("lbl_saudi_currency") : ""}
                        </span>
                      </TooltipWrapper>
                    </div>
                  </div>
                  {showCashoutTime && (
                    <div className={classNames(styles.cell, styles.colWafyFee)}>
                      <span className={classNames(styles.normalText)}>
                        {row?.cashoutTime
                          ? new Date(row?.cashoutTime).toLocaleString("ar-EG", {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: true,
                            })
                          : "-"}
                      </span>
                    </div>
                  )}
                  {showReferenceNumber && (
                    <div
                      className={classNames(
                        styles.cell,
                        styles.colReferenceNumber
                      )}>
                      <div className={styles.cellWithError}>
                        <TooltipWrapper
                          id="bank_ref_number_id_tooltip"
                          content={
                            row?.referenceNumber ? row?.referenceNumber : "-"
                          }>
                          <span className={classNames(styles.normalText)}>
                            {row?.referenceNumber ? row?.referenceNumber : "-"}
                          </span>
                        </TooltipWrapper>
                      </div>
                    </div>
                  )}
                  {showStatus && (
                    <div className={classNames(styles.cell, styles.colStatus)}>
                      <span
                        className={styles.statusPill}
                        style={{
                          background:
                            partiesStatuses?.[String(row?.status)]?.color,
                        }}>
                        <p
                          style={{
                            color:
                              partiesStatuses?.[String(row?.status)]?.textColor,
                          }}>
                          {partiesStatuses?.[String(row?.status)]?.title}
                        </p>
                      </span>
                    </div>
                  )}
                  {hasOnBehalfColumn && (
                    <div className={classNames(styles.cell, styles.colName)}>
                      <span className={classNames(styles.normalText)}>
                        {showOnBehalf ? row?.onBehalfByName ?? "-" : ""}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PartyTable;
